// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../Header";
import Footer from "../footer";
import "./Auth.css";

export default function ForgotPassword() {
    const [step, setStep] = useState(1); // 1: email, 2: OTP & password
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [countdown, setCountdown] = useState(0);
    const [verifiedEmail, setVerifiedEmail] = useState("");
    const [emailExists, setEmailExists] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    
    // Validation states
    const [emailError, setEmailError] = useState("");
    const [otpError, setOtpError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    const navigate = useNavigate();

    // Countdown timer effect
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    // Debounced email validation effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (email && !emailError) {
                checkEmailExists(email);
            }
        }, 1000); // Wait 1 second after user stops typing

        return () => clearTimeout(timeoutId);
    }, [email, emailError]); // eslint-disable-line react-hooks/exhaustive-deps

    // Check if email exists in database
    const checkEmailExists = async (emailToCheck) => {
        if (!emailToCheck || !validateEmail(emailToCheck)) {
            setEmailExists(false);
            return false;
        }

        setCheckingEmail(true);
        try {
            const response = await axios.post(
                "https://harivishnu-in.onrender.com/api/forgot-password/check-email",
                { email: emailToCheck.toLowerCase() }
            );
            
            if (response.data.exists) {
                setEmailExists(true);
                setEmailError("");
                return true;
            } else {
                setEmailExists(false);
                setEmailError("Email address not found. Please check your email or sign up.");
                return false;
            }
        } catch (err) {
            console.error("Check email error:", err);
            setEmailExists(false);
            // Don't show error for network issues, just don't verify
            if (err.response && err.response.status === 404) {
                setEmailError("Email address not found. Please check your email or sign up.");
            } else {
                // For other errors, don't block the user, just don't verify
                setEmailError("");
            }
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    // Allow user to proceed even if email verification fails
    const canProceedWithOTP = () => {
        return email && !emailError && (emailExists || !checkingEmail);
    };

    // Validation functions
    const validateEmail = (value) => {
        if (!value) {
            setEmailError("Email is required");
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            setEmailError("Please enter a valid email address");
            return false;
        }
        setEmailError("");
        return true;
    };

    const validateOTP = (value) => {
        if (!value) {
            setOtpError("OTP is required");
            return false;
        }
        if (value.length !== 6 || !/^\d{6}$/.test(value)) {
            setOtpError("Please enter a valid 6-digit OTP");
            return false;
        }
        setOtpError("");
        return true;
    };

    const validatePassword = (value) => {
        if (!value) {
            setPasswordError("Password is required");
            return false;
        }
        if (value.length < 8) {
            setPasswordError("Password must be at least 8 characters long");
            return false;
        }
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
            setPasswordError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
            return false;
        }
        setPasswordError("");
        return true;
    };

    const validateConfirmPassword = (value) => {
        if (!value) {
            setConfirmPasswordError("Please confirm your password");
            return false;
        }
        if (value !== password) {
            setConfirmPasswordError("Passwords do not match");
            return false;
        }
        setConfirmPasswordError("");
        return true;
    };

    const handleRequestOTP = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Validate email format first
        if (!validateEmail(email)) {
            return;
        }

        // Don't check email existence again - UI already handles this
        // Just proceed with sending OTP if email format is valid

        setLoading(true);
        try {
            const response = await axios.post(
                "https://harivishnu-in.onrender.com/api/forgot-password/request-otp",
                { email: email.toLowerCase() }
            );

            if (response.data.success) {
                setSuccess("OTP sent to your email address");
                setStep(2);
                setVerifiedEmail(email.toLowerCase());
                setCountdown(60); // Start 60-second countdown
            } else {
                setError(response.data.message || "Failed to send OTP");
            }
        } catch (err) {
            console.error("Request OTP error:", err);
            setError(
                (err.response && err.response.data && err.response.data.message) ||
                "Failed to send OTP. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setError("");
        // Don't clear success message during resend to prevent container from disappearing
        setResendLoading(true);
        
        try {
            const response = await axios.post(
                "https://harivishnu-in.onrender.com/api/forgot-password/request-otp",
                { email: email.toLowerCase() }
            );

            if (response.data.success) {
                setSuccess("OTP resent to your email address");
                setCountdown(60); // Start 60-second countdown
            } else {
                setError(response.data.message || "Failed to resend OTP");
            }
        } catch (err) {
            console.error("Resend OTP error:", err);
            setError(
                (err.response && err.response.data && err.response.data.message) ||
                "Failed to resend OTP. Please try again."
            );
        } finally {
            setResendLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!validateOTP(otp) || !validatePassword(password) || !validateConfirmPassword(confirmPassword)) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                "https://harivishnu-in.onrender.com/api/forgot-password/verify-otp",
                {
                    email: email.toLowerCase(),
                    otp,
                    password
                }
            );

            if (response.data.success) {
                setSuccess("Password reset successful! You can now login with your new password.");
                setTimeout(() => {
                    navigate("/signin");
                }, 2000);
            } else {
                setError(response.data.message || "Failed to reset password");
            }
        } catch (err) {
            console.error("Reset password error:", err);
            setError(
                (err.response && err.response.data && err.response.data.message) ||
                "Failed to reset password. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleBackToEmail = () => {
        setStep(1);
        setOtp("");
        setPassword("");
        setConfirmPassword("");
        setError("");
        setSuccess("");
        setCountdown(0);
        setVerifiedEmail("");
        setEmailExists(false);
    };

    // Handle email input change with real-time validation
    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        
        // Clear previous email existence check when email changes
        if (newEmail !== verifiedEmail) {
            setEmailExists(false);
        }
        
        // Real-time format validation
        validateEmail(newEmail);
    };

    // Check if email has changed from the verified email
    const isEmailChanged = verifiedEmail && email.toLowerCase() !== verifiedEmail;

    return (
        <>
            <Header />
            <div className="auth-container forgot-password-container mx-auto my-10 w-full max-w-2xl rounded-2xl bg-white/95 p-6 shadow-lg backdrop-blur-sm md:p-8">
                <div className="auth-header mb-4">
                    <h2 className="heading-1 text-3xl font-bold text-slate-900">Forgot Password</h2>
                    <p className="auth-description mt-2 text-sm leading-relaxed text-slate-700">
                        {step === 1 
                            ? "Enter your email address to receive a reset code"
                            : "Enter the OTP sent to your email and set a new password"
                        }
                    </p>
                </div>

                {error && <p className="validation-message invalid">{error}</p>}
                {success && (
                    <div className="success-message-container">
                        <p className="validation-message valid">{success}</p>
                        {step === 2 && (
                            <button 
                                type="button" 
                                className="resend-otp-btn"
                                onClick={handleResendOTP}
                                disabled={resendLoading || countdown > 0}
                            >
                                {resendLoading ? (
                                    <>
                                        <span className="resend-otp-spinner"></span>
                                        Resending...
                                    </>
                                ) : 
                                 countdown > 0 ? `Resend OTP (${countdown}s)` : "Resend OTP"}
                            </button>
                        )}
                    </div>
                )}

                {step === 1 ? (
                    // Step 1: Email Input
                    <form className="space-y-4" onSubmit={handleRequestOTP}>
                        <div className="email-input">
                            <label htmlFor="email">Email Address</label>
                            <input
                                id="email"
                                type="email"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={handleEmailChange}
                                className={emailError ? "invalid" : email && emailExists ? "valid" : ""}
                            />
                            {emailError && <span className="validation-message invalid">{emailError}</span>}
                            {checkingEmail && <span className="validation-message">Checking email...</span>}
                            {email && !emailError && !checkingEmail && emailExists && (
                                <span className="validation-message valid">✓ Email verified</span>
                            )}
                            {email && !emailError && !checkingEmail && !emailExists && (
                                <span className="validation-message warning">
                                    ⚠️ Email verification failed. You can still proceed if you're sure this is your email.
                                </span>
                            )}
                            {isEmailChanged && (
                                <span className="validation-message warning">
                                    ⚠️ Email address has changed. Please verify this is the correct email.
                                </span>
                            )}
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading || !canProceedWithOTP() || isEmailChanged}
                        >
                            {loading ? "Sending OTP..." : "Send OTP"}
                        </button>
                    </form>
                ) : (
                    // Step 2: OTP and Password Input
                    <form className="otp-password-form space-y-4" onSubmit={handleResetPassword}>
                        <div className="email-display">
                            <label>Email Address</label>
                            <div className="email-row">
                                <span className="email-value">{email}</span>
                                <span className="email-edit" onClick={handleBackToEmail}>Edit</span>
                            </div>
                        </div>

                        <div className="otp-input">
                            <label htmlFor="otp">OTP Code</label>
                            <input
                                id="otp"
                                type="text"
                                placeholder="Enter 6-digit OTP"
                                value={otp}
                                onChange={(e) => {
                                    setOtp(e.target.value);
                                    validateOTP(e.target.value);
                                }}
                                onBlur={(e) => validateOTP(e.target.value)}
                                maxLength={6}
                                className={otpError ? "invalid" : otp ? "valid" : ""}
                            />
                            {otpError && <span className="validation-message invalid">{otpError}</span>}
                        </div>

                        <div className="password-input">
                            <label htmlFor="password">New Password</label>
                            <div className="password-field">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter new password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        validatePassword(e.target.value);
                                    }}
                                    onBlur={(e) => validatePassword(e.target.value)}
                                    className={passwordError ? "invalid" : password ? "valid" : ""}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
                                </button>
                            </div>
                            {passwordError && <span className="validation-message invalid">{passwordError}</span>}
                        </div>

                        <div className="password-input">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className="password-field">
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        validateConfirmPassword(e.target.value);
                                    }}
                                    onBlur={(e) => validateConfirmPassword(e.target.value)}
                                    className={confirmPasswordError ? "invalid" : confirmPassword ? "valid" : ""}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    <i className={`fas fa-${showConfirmPassword ? 'eye-slash' : 'eye'}`}></i>
                                </button>
                            </div>
                            {confirmPasswordError && <span className="validation-message invalid">{confirmPasswordError}</span>}
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading || !otp || !password || !confirmPassword || !!otpError || !!passwordError || !!confirmPasswordError}
                        >
                            {loading ? "Resetting Password..." : "Reset Password"}
                        </button>
                    </form>
                )}

                <div className="auth-links mt-5 border-t border-slate-200 pt-4">
                    <p>
                        Remember your password?{" "}
                        <a href="/signin" onClick={(e) => {
                            e.preventDefault();
                            navigate("/signin");
                        }}>
                            Sign in here
                        </a>
                    </p>
                </div>
            </div>
            <Footer />
        </>
    );
} 
